import React, { useState, useEffect, Fragment, ReactNode } from "react";
import ProductsActionCreators from "@/Actions/Products";
import UsersActionCreators from "@/Actions/User";
import EmptyProductPricingPlansIcon from "@/Assets/ProductsPage/EmptyProductPricingPlansIcon.svg";
import RenderCustomFieldFormItem from "@molecules/ZRenderCustomFields";
import RoleBasedDisplayWrapper from "@/Components/UI/RoleBasedDisplayWrapper";
import { checkCanMultiplePricingPlansFor } from "@/Components/Utils";
import { useFeatureFlag } from "@/Hooks/useFeature";
import useSuperOpsCrudLockout from "@/Hooks/useSuperOpsCrudLockout";
import {
	handleMarginChange,
	handleMarkupChange,
	handleMarkupMargin
} from "@/Components/Utils/PricingCalculator";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import { updateProductTemplates } from "@/Services/Products";
import { Form, Icon } from "@ant-design/compatible";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import TaxSelect from "@molecules/TaxSelect";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import ZEditOrCloneProductTemplateUpdateTable from "@templates/ZProductDetails/ZEditOrCloneProductTemplateUpdateTable";
import ZCheckbox from "@atoms/ZCheckbox";
import { Document } from "@/Contracts/Documents";
import {
	AddOrEditProductRequest,
	BundleProduct,
	commonProductFormKeys,
	Product,
	ProductBillingType,
	productBillingTypeOptions,
	ProductFormValues,
	ProductSource,
	ProductTemplates,
	ProductType
} from "@/Contracts/Products";
import {
	CustomField,
	CustomFieldMandatoryFieldDetailsValues
} from "@/Contracts/Settings";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import ZMessage from "@molecules/ZMessage";
import {
	currencyFormatter,
	getCurrencySettings,
	percentageFormatter
} from "@/Utils/Currency";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Col from "antd/lib/col";
import Divider from "antd/lib/divider";
import { DrawerProps } from "antd/lib/drawer";
import Input from "antd/lib/input";
import TextArea from "antd/lib/input/TextArea";
import Radio, { RadioChangeEvent } from "antd/lib/radio";
import Row from "antd/lib/row";
import Skeleton from "antd/lib/skeleton";
import he from "he";
import { useSelector } from "react-redux";
import "./ProductsDrawer.less";
import { processCustomFieldFormValues } from "@/Utils/common";
import { ZDrawer, ZInput } from "@molecules";
import {
	ZInputNumber,
	ZButton,
	ZSelect,
	ZModal,
	ZTypography,
	ZTooltip
} from "@atoms";
import useDispatch from "@/Components/Utils/useDispatch";

const { ZText } = ZTypography;

export type Template = { name: string; id: string };
export interface Props
	extends DrawerProps,
		FormComponentProps<ProductFormValues> {
	readonly currentActiveProduct: BundleProduct | null;
	readonly currentActiveDocument: Document | null;
	readonly onSubmit: (product: BundleProduct) => Promise<void>;
	readonly isReadonly?: boolean;
	readonly onClose: () => void;
	readonly hasTaxEditPermission?: boolean;
	readonly bundledProductList: BundleProduct[];
}

export const ConfigureBundleChildProductDrawerForm: React.FC<Props> = props => {
	const isLocked = useSuperOpsCrudLockout();
	const hasTaxEditPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.TAX_SETUP_UPDATE]
	);

	const movePreferencesToGlobal = useFeatureFlag("move_preferences_global");
	const isBundleChildEditFixEnabled = useFeatureFlag(
		"fix_bundle_child_edit_isolation"
	);
	const {
		isLoading,
		taxCategoriesList,
		addTaxCategoryDrawerOpen,
		defaultPreferences,
		unitOfMeasureMapping,
		customFieldListProduct
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.recentActivityFeed.isLoading,
			taxCategoriesList: state.user.taxCategoriesList,
			addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen,
			defaultPreferences: movePreferencesToGlobal
				? state.user.userCompany.settings.preference_settings
						?.product_preference_settings
				: state.user.userData?.user_settings?.preference_settings
						?.product_preference_settings,
			unitOfMeasureMapping:
				state.user.userCompany?.settings
					.default_unit_of_measure_mapping,
			userCompany: state.user.userCompany,
			userData: state.user.userData,
			customFieldListProduct: state.customFields.customFieldList.item
		};
	});
	const dispatch = useDispatch();

	const [currentActiveProductImage, setCurrentActiveProductImage] = useState<
		string | null
	>(null);
	const [productImagePreviewModalOpen, setProductImagePreviewModalOpen] =
		useState(false);
	const [customFieldList, setCustomFieldList] = useState<CustomField[]>(
		new Array<CustomField>()
	);
	const [updateExistingTemplateChecked, setUpdateExistingTemplateChecked] =
		useState(false);
	const [, setEditedProduct] = useState<Product | null>(null);
	const [templatesToUpdate, setTemplatesToUpdate] = useState<Template[]>([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [checkedList, setCheckedList] = useState<string[]>([]);
	const [editChildProductRequest, setEditChildProductRequest] =
		useState<BundleProduct | null>(null);

	const showAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerOpened());
	};

	const closeAddTaxCategoryDrawer = () => {
		dispatch(UsersActionCreators.addTaxCategoryDrawerClosed());
	};

	useEffect(() => {
		const { currentActiveProduct, visible } = props;
		if (currentActiveProduct && visible) {
			getCustomFields(true);
		}
	}, [props.visible]);

	const getCustomFields = async (updateMarkupMargin: boolean) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			setCustomFieldList(customFieldListProduct);
			if (updateMarkupMargin && props.currentActiveProduct) {
				const defaultData = props.currentActiveProduct;
				const { markup, margin } = handleMarkupMargin(
					defaultData?.selected_cost_price ?? 0,
					defaultData?.sell_price ?? 0,
					defaultData.quantity,
					defaultData?.should_add_setup_price_to_margin ?? false,
					defaultData?.selected_setup_price ?? 0
				);
				props.form.setFieldsValue(
					handleMarkupMargin(
						props.currentActiveProduct.selected_cost_price || 0,
						props.currentActiveProduct.sell_price || 0,
						props.currentActiveProduct.quantity || 1,
						props.currentActiveProduct
							.should_add_setup_price_to_margin || true,
						props.currentActiveProduct.selected_setup_price || 0
					)
				);
				props.form.setFieldsValue({
					margin: margin ?? defaultPreferences?.margin,
					markup: markup ?? defaultPreferences?.markup
				});
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	const showPreviewProductImageModal = (productImage: string) => {
		setProductImagePreviewModalOpen(true);
		setCurrentActiveProductImage(productImage);
	};

	const closePreviewProductImageModal = () => {
		setProductImagePreviewModalOpen(false);
		setCurrentActiveProductImage(null);
	};

	const onClose = () => {
		if (props.onClose) {
			props.onClose();
		}
	};

	const onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		props.form.validateFieldsAndScroll(
			{
				scroll: {
					alignWithTop: true
				}
			},
			(errors: unknown, editProductFormValues: ProductFormValues) => {
				if (!!errors) {
					ZMessage.error("Please check the form values");
					return;
				}

				const isProductExistInBundle = props.bundledProductList.find(
					bundleProduct =>
						bundleProduct.item_id ===
						props.currentActiveProduct?.item_id
				);

				if (isProductExistInBundle) {
					ZModal.confirm({
						title: "Please Confirm",
						content:
							"Product already added to the bundle. Update the product with new values?",
						okText: "Yes",
						onOk: () => {
							editProduct($event, editProductFormValues);
						}
					});
				} else {
					editProduct($event, editProductFormValues);
				}
			}
		);
	};

	const editProduct = async (
		_event: any,
		editProductFormValues: ProductFormValues
	) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			if (!props.currentActiveProduct) {
				throw new Error("No default value on the drawer");
			}
			let editChildProductRequest: BundleProduct = {
				...props.currentActiveProduct,
				...editProductFormValues,
				mandatory_custom_field_check_require: true
			};

			const customFieldFormValues =
				editProductFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				editChildProductRequest = {
					...editChildProductRequest,
					item: {
						...editChildProductRequest.item,
						custom_fields: processCustomFieldFormValues(
							customFieldFormValues,
							customFieldList
						),
						mandatory_custom_field_check_require: true
					}
				};
			}

			const editProductRequest: Product = {
				...props.currentActiveProduct.item,
				...editProductFormValues,
				bundled_items: [],
				custom_fields: editChildProductRequest.item.custom_fields,
				mandatory_custom_field_check_require: true
			};

			setEditChildProductRequest(editChildProductRequest);

			if (
				!editChildProductRequest.item_id.includes(
					ProductSource.Etilize
				) &&
				!editChildProductRequest.item_id.includes("distributor")
			) {
				if (isBundleChildEditFixEnabled) {
					await props.onSubmit(editChildProductRequest);
				} else {
					const editProductResponse = await dispatch(
						ProductsActionCreators.editProduct(editProductRequest)
					);
					setEditedProduct(editProductResponse.editProduct);

					if (
						updateExistingTemplateChecked &&
						!!editProductResponse.editProduct.templates?.length
					) {
						showModal(editProductResponse.editProduct.templates);
					} else {
						await props.onSubmit(editChildProductRequest);
					}
				}
			} else {
				const addProductRequest = new AddOrEditProductRequest();
				addProductRequest.name = editProductRequest.name;
				addProductRequest.description = editProductRequest.description;
				addProductRequest.type = editProductRequest.type;
				addProductRequest.product_category =
					editProductRequest.product_category;
				addProductRequest.manufacturer =
					editProductRequest.manufacturer;
				addProductRequest.manufacturer_part_no =
					editProductRequest.manufacturer_part_no;
				addProductRequest.item_images = editProductRequest.item_images;
				addProductRequest.vendor_id = editProductRequest.vendor_id;
				addProductRequest.tax_category_id =
					editProductRequest.tax_category_id;
				addProductRequest.show_individual_product_pricing =
					editProductRequest.show_individual_product_pricing;
				addProductRequest.should_add_setup_price_to_margin =
					editProductRequest.should_add_setup_price_to_margin;
				addProductRequest.unit_of_measure =
					editProductRequest.unit_of_measure;
				addProductRequest.mandatory_custom_field_check_require = true;
				addProductRequest.custom_fields =
					editProductRequest.custom_fields;
				if (!isLocked) {
					await dispatch(
						ProductsActionCreators.addProduct(addProductRequest)
					);
				}
				await props.onSubmit(editChildProductRequest);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setUpdateExistingTemplateChecked(false);
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	const showModal = (templates: Template[]) => {
		setTemplatesToUpdate([...templates]);
		setIsModalVisible(true);
	};

	const getFieldValues = (): Record<commonProductFormKeys, any> => {
		const keys: commonProductFormKeys[] = [
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"type",
			"margin",
			"markup",
			"show_individual_product_pricing"
		];
		return props.form.getFieldsValue(keys) as Record<
			commonProductFormKeys,
			any
		>;
	};

	const onCostPriceChange = (newCostPrice?: number | string | null): void => {
		if (newCostPrice === undefined || newCostPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onMarginChange = (newMargin?: number | string | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().selected_cost_price) !== 0
		) {
			return;
		}
		props.form.setFieldsValue(
			handleMarginChange(values, Number(newMargin))
		);
	};

	const onMarkupChange = (newMarkup?: number | string | null): void => {
		if (
			newMarkup === undefined ||
			props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupChange(values, Number(newMarkup))
		);
	};

	const renderCostPriceInputGroup = () => {
		const {
			currentActiveProduct,
			form: { getFieldDecorator, getFieldValue }
		} = props;

		const defaultData = {
			...currentActiveProduct?.item,
			...currentActiveProduct
		} as BundleProduct;

		const isReadonly = !!props.isReadonly;

		const { markup, margin } = handleMarkupMargin(
			defaultData?.selected_cost_price ?? 0,
			defaultData?.sell_price ?? 0,
			defaultData.quantity,
			defaultData?.should_add_setup_price_to_margin ?? false,
			defaultData?.selected_setup_price ?? 0
		);
		return (
			<Fragment>
				<Col span={24}>
					<Form.Item label="Cost Price" labelAlign="left">
						<Input.Group>
							{getFieldDecorator("selected_cost_price", {
								rules: [
									{
										required: true,
										validator: (_, value) => {
											if (Number(value) < 0) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Cost price cannot be less than 0"
													)
												);
											}
											if (!value && value !== 0) {
												return Promise.reject(
													new Error(
														"Cost price cannot be empty"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue:
									defaultData.selected_cost_price || 0
							})(
								<ZInputNumber
									disabled={isReadonly}
									type="number"
									step=".01"
									placeholder="Please enter product cost price"
									min={0}
									className="has-currency-addon"
									onChange={onCostPriceChange}
									precision={2}
								/>
							)}
							<ZInput
								placeholder={getCurrencySettings().currency}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Markup"
						labelAlign="left"
						hasFeedback={true}
					>
						<Input.Group>
							{getFieldDecorator("markup", {
								rules: [
									{
										required: false,
										type: "number"
									}
								],
								initialValue:
									markup ?? defaultPreferences?.markup ?? 0
							})(
								<ZInputNumber
									disabled={isReadonly}
									placeholder="Please enter product Markup"
									className="full-width"
									onChange={onMarkupChange}
									precision={2}
								/>
							)}
							<ZInput
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Margin"
						labelAlign="left"
						hasFeedback={true}
					>
						<Input.Group>
							{getFieldDecorator("margin", {
								rules: [
									{
										required: false,
										validator(_, value) {
											if (
												Number(value) >= 100 &&
												getFieldValue(
													"selected_cost_price"
												) > 0
											) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Margin cannot be 100% unless cost price is 0"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue:
									margin ?? defaultPreferences?.margin ?? 0
							})(
								<ZInputNumber
									disabled={isReadonly}
									placeholder="Please enter product Margin"
									className="full-width"
									max={100}
									onChange={onMarginChange}
									precision={2}
									step={0.1}
								/>
							)}
							<ZInput
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
			</Fragment>
		);
	};

	const onSetupPriceChange = (
		newSetupPrice?: number | string | null
	): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	const onQuantityChange = (newQuantity?: number | string | null): void => {
		if (newQuantity === undefined || newQuantity === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				Number(newQuantity),
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onSellPriceChange = (newSellPrice?: number | string | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const renderSellPriceInputGroup = () => {
		const {
			form: { getFieldDecorator },
			currentActiveProduct
		} = props;

		const defaultData = {
			...currentActiveProduct?.item,
			...currentActiveProduct
		} as BundleProduct;

		const isReadonly = !!props.isReadonly;

		return (
			<Fragment>
				<Col span={24}>
					<Form.Item
						label="Billing Period"
						labelAlign="left"
						hasFeedback={true}
					>
						{getFieldDecorator("selected_billing_period", {
							initialValue:
								defaultData.selected_billing_period ||
								ProductBillingType.OneTime
						})(
							<ZSelect<ProductBillingType>
								getPopupContainer={trigger =>
									trigger.parentNode
								}
								disabled={isReadonly}
								selectOptions={(
									Object.keys(
										productBillingTypeOptions
									) as ProductBillingType[]
								).map((value, index) => ({
									name: value,
									value: value,
									key: `${value}-${index}`
								}))}
							/>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Quantity"
						labelAlign="left"
						hasFeedback={true}
					>
						{getFieldDecorator("quantity", {
							rules: [
								{
									required: true,
									message: "Please enter product quantity"
								}
							],
							initialValue: defaultData.quantity || 1
						})(
							<ZInputNumber
								disabled={isReadonly}
								type="number"
								placeholder="Please enter product quantity"
								min={1}
								className="full-width"
								onChange={onQuantityChange}
								precision={0}
							/>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item label="Sell Price" labelAlign="left">
						<Input.Group>
							{getFieldDecorator("sell_price", {
								rules: [
									{
										required: true,
										message:
											"This is not a valid price for this product"
									}
								],
								initialValue: defaultData.sell_price || 0
							})(
								<ZInputNumber
									disabled={isReadonly}
									type="number"
									step=".01"
									placeholder="Please enter product sell price"
									min={0}
									className="has-currency-addon"
									onChange={onSellPriceChange}
									precision={2}
								/>
							)}
							<ZInput
								placeholder={getCurrencySettings().currency}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item label="Setup Fee" labelAlign="left">
						<Input.Group>
							{getFieldDecorator("selected_setup_price", {
								rules: [
									{
										required: true,
										message:
											"Please enter product setup fee"
									}
								],
								initialValue:
									defaultData.selected_setup_price ?? 0
							})(
								<ZInputNumber
									disabled={isReadonly}
									step=".01"
									type="number"
									placeholder="Please enter product setup fee"
									min={0}
									className="has-currency-addon"
									onChange={onSetupPriceChange}
									precision={2}
								/>
							)}
							<ZInput
								placeholder={getCurrencySettings().currency}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
			</Fragment>
		);
	};

	const onAddSetupPriceToMarginChange = (e: CheckboxChangeEvent): void => {
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				e.target.checked,
				values.selected_setup_price
			)
		);
	};

	const handlePricingChange = (event: RadioChangeEvent) => {
		const selectedPricing = props.currentActiveProduct?.item.pricing.find(
			curr => curr.id === event.target.value
		);
		const values = getFieldValues();

		const partialUpdate: Partial<BundleProduct> = {
			selected_billing_period: selectedPricing?.billing_period,
			selected_cost_price: selectedPricing?.cost_price,
			sell_price: selectedPricing?.sell_price,
			selected_setup_price: selectedPricing?.setup_price,
			...handleMarkupMargin(
				selectedPricing?.cost_price || 0,
				selectedPricing?.sell_price || 0,
				values.quantity,
				values.should_add_setup_price_to_margin,
				selectedPricing?.setup_price || 0
			)
		};

		props.form.setFieldsValue(partialUpdate);
	};

	const getInitialTaxCategoryId = (
		defaultData: BundleProduct
	): string | undefined => {
		if (props.currentActiveDocument?.tax_region_id) {
			if (
				defaultData?.tax_category_id &&
				defaultData?.tax_category_id?.length > 0
			) {
				return defaultData?.tax_category_id;
			} else if (defaultData?.product_source === ProductSource.Etilize) {
				return taxCategoriesList?.find(
					taxCategory =>
						!taxCategory.disabled &&
						taxCategory.default_for_types?.includes(
							ProductType.Hardware
						)
				)?.id;
			} else if (defaultData?.type && defaultData?.type?.length > 0) {
				return taxCategoriesList?.find(
					taxCategory =>
						!taxCategory.disabled &&
						taxCategory.default_for_types?.includes(
							defaultData?.type as ProductType
						)
				)?.id;
			}
		} else {
			if (!!defaultData?.tax_category_id?.length) {
				return defaultData?.tax_category_id;
			} else if (defaultData?.product_source === ProductSource.Etilize) {
				return taxCategoriesList?.find(
					taxCategory =>
						!taxCategory.disabled &&
						taxCategory.default_for_types?.includes(
							ProductType.Hardware
						)
				)?.id;
			} else if (defaultData?.type && defaultData?.type?.length > 0) {
				return taxCategoriesList?.find(
					taxCategory =>
						!taxCategory.disabled &&
						taxCategory.default_for_types?.includes(
							defaultData?.type as ProductType
						)
				)?.id;
			}
		}
		return undefined;
	};

	const getInitialUnitOfMeasure = (
		defaultData: BundleProduct
	): string | undefined => {
		if (!!defaultData.item.unit_of_measure?.length) {
			return defaultData.item.unit_of_measure;
		} else if (!!defaultData.item.type?.length) {
			return unitOfMeasureMapping?.find(
				mapping => mapping.item_type === defaultData.item.type
			)?.unit_of_measure;
		}
		return undefined;
	};

	const getProductFormItem = (customField: CustomField): ReactNode => {
		const isReadonly = !!props.isReadonly;
		const defaultData = {
			...props.currentActiveProduct?.item,
			...props.currentActiveProduct
		} as BundleProduct;
		const canHaveMultiplePricingPlans = checkCanMultiplePricingPlansFor(
			props.form.getFieldValue("type")
		);
		const values = getFieldValues();
		const { margin } = handleMarkupMargin(
			values.selected_cost_price || 0,
			values.sell_price || 0,
			values.quantity || 1,
			!!values.should_add_setup_price_to_margin,
			values.selected_setup_price || 0
		);
		const marginAmount =
			((values.sell_price || 0) - (values.selected_cost_price || 0)) *
				(values.quantity || 1) +
			(!!values.should_add_setup_price_to_margin
				? values.selected_setup_price || 0
				: 0);
		switch (customField.system_field_id) {
			case "item_type": {
				return <Fragment />;
			}
			case "item_item_images": {
				return (
					<Fragment>
						<Col span={6}>
							<ZText className="mt-6">Product Images:</ZText>
						</Col>
						<Col span={18} className="margin-bottom-32px">
							<div className="product-image-upload-container">
								{!!defaultData?.item.item_images?.length &&
									defaultData?.item.item_images.map(
										productImage => {
											return (
												<div
													key={productImage}
													className="product-image-container"
												>
													<img
														src={productImage}
														alt="Product"
														className="product-image"
													/>
													<div className="product-image-overlay">
														<ZTooltip title="Preview product image">
															<Icon
																type="eye"
																onClick={() => {
																	showPreviewProductImageModal(
																		productImage
																	);
																}}
															/>
														</ZTooltip>
													</div>
												</div>
											);
										}
									)}
							</div>
							{!!currentActiveProductImage?.length && (
								<ZModal
									visible={productImagePreviewModalOpen}
									footer={null}
									onCancel={closePreviewProductImageModal}
									className="product-image-preview-modal"
								>
									<img
										src={currentActiveProductImage}
										alt="Product"
										className="product-image-preview"
									/>
								</ZModal>
							)}
						</Col>
					</Fragment>
				);
			}
			case "item_description": {
				return (
					<Col span={24}>
						<Form.Item
							label="Description"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("description", {
								initialValue: he.decode(
									defaultData?.description ?? ""
								)
							})(
								<TextArea
									disabled={isReadonly}
									placeholder="Please enter product description"
									autoSize={{ minRows: 3 }}
									allowClear={true}
									className="zomentum-sm-textarea"
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_pricing": {
				return (
					<Fragment>
						{canHaveMultiplePricingPlans && (
							<Col span={24}>
								{(!defaultData?.item?.pricing ||
									defaultData?.item?.pricing?.length ===
										0) && (
									<div className="align-center mt-6">
										<EmptyProductPricingPlansIcon />
										<ZText className="empty-component-text mt-6">
											This product has no pricing plan to
											display.
										</ZText>
									</div>
								)}
								<Form.Item
									label="Pricing Plan"
									labelAlign="left"
								>
									{props.form.getFieldDecorator(
										"selected_pricing_id",
										{
											initialValue:
												defaultData?.selected_pricing_id
										}
									)(
										<Radio.Group
											disabled={isReadonly}
											onChange={handlePricingChange}
											className="pricing-radio-plan-radio-group"
										>
											{defaultData?.item?.pricing?.map(
												pricing => (
													<Radio
														key={pricing.id || ""}
														value={pricing.id}
													>
														{pricing.name}
													</Radio>
												)
											)}
										</Radio.Group>
									)}
								</Form.Item>
							</Col>
						)}
					</Fragment>
				);
			}
			case "item_product_category": {
				return <Fragment />;
			}
			case "item_manufacturer": {
				return <Fragment />;
			}
			case "item_manufacturer_part_no": {
				return <Fragment />;
			}
			case "item_vendor_name": {
				return <Fragment />;
			}
			case "item_vendor_sku_no": {
				return <Fragment />;
			}
			case "item_tax_category_id": {
				return (
					<Col span={24}>
						<Form.Item
							label="Tax Category"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("tax_category_id", {
								rules: [
									{
										required: true,
										message: "Please enter tax category"
									}
								],
								initialValue:
									getInitialTaxCategoryId(defaultData)
							})(
								<TaxSelect
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									placeholder="Please select tax category"
									loading={isLoading}
									disabled={isLoading || isReadonly}
									dropdownRender={menu => (
										<div>
											{menu}
											<RoleBasedDisplayWrapper
												requiredRole={
													UserRole.PrimaryRole
												}
											>
												{hasTaxEditPermission && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={
															showAddTaxCategoryDrawer
														}
													>
														Add Tax Category
													</div>
												)}
											</RoleBasedDisplayWrapper>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_should_add_setup_price_to_margin": {
				return (
					<Fragment>
						{!canHaveMultiplePricingPlans && (
							<Fragment>
								<Col span={24} className="mb-6">
									<Row className="pricing-data-box">
										{renderCostPriceInputGroup()}
										<Divider className="mb-6" />
										{renderSellPriceInputGroup()}
										<Col span={24}>
											{props.form.getFieldDecorator(
												"should_add_setup_price_to_margin",
												{
													valuePropName: "checked",
													initialValue: true
												}
											)(
												<ZCheckbox
													disabled={isReadonly}
													onChange={
														onAddSetupPriceToMarginChange
													}
												>
													Add 'Setup Fee' to Margin
												</ZCheckbox>
											)}
											<div className="margin-preview">
												<strong>Item Margin:</strong>{" "}
												<span>
													{percentageFormatter(
														margin,
														100
													)}{" "}
													(
													{currencyFormatter(
														marginAmount
													)}
													)
												</span>
											</div>
										</Col>
									</Row>
								</Col>
							</Fragment>
						)}
						{canHaveMultiplePricingPlans && (
							<>
								{renderCostPriceInputGroup()}
								{renderSellPriceInputGroup()}
							</>
						)}
						{canHaveMultiplePricingPlans && (
							<Col span={24}>
								{props.form.getFieldDecorator(
									"should_add_setup_price_to_margin",
									{
										valuePropName: "checked",
										initialValue: true
									}
								)(
									<ZCheckbox
										disabled={isReadonly}
										onChange={onAddSetupPriceToMarginChange}
									>
										Add 'Setup Fee' to Margin
									</ZCheckbox>
								)}
								<ZText className="margin-preview mt-6 mb-6">
									<strong>Item Margin:</strong>{" "}
									<span>
										{percentageFormatter(margin, 100)} (
										{currencyFormatter(marginAmount)})
									</span>
								</ZText>
							</Col>
						)}
					</Fragment>
				);
			}
			case "item_show_individual_product_pricing":
			case "item_tax_type":
				return <Fragment />;
			case "item_unit_of_measure": {
				const uomOptions = customField.allowed_dropdown_values?.map(
					value => ({
						value,
						name: value
					})
				);
				const initialUnitOfMeasure =
					getInitialUnitOfMeasure(defaultData);
				return (
					<Col span={24}>
						<Form.Item
							label="UOM"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("unit_of_measure", {
								initialValue: initialUnitOfMeasure,
								rules: [
									{
										required:
											customField
												?.mandatory_field_details?.[
												CustomFieldMandatoryFieldDetailsValues
													.FOR_UI
											],
										message:
											"Please select the unit of measure"
									}
								]
							})(
								<ZSelect
									placeholder="Please select the unit of measure"
									data-testid="unit_of_measure_select"
									allowClear
									selectOptions={uomOptions}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			default: {
				const productCustomField =
					props.currentActiveProduct?.item?.custom_fields?.find(
						field => field.display_name === customField.display_name
					);
				const values = productCustomField?.values;
				const isDataEditable =
					customField?.is_data_editable_field ?? true;
				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={props.form}
							customField={customField}
							values={values}
							isReadonly={!!props.isReadonly || !isDataEditable}
							customFieldList={customFieldList}
							formAlignment={{
								labelCol: { span: 6 },
								wrapperCol: { span: 18 }
							}}
						/>
					</Col>
				);
			}
		}
	};

	const handleOk = async () => {
		const { currentActiveProduct } = props;

		if (currentActiveProduct) {
			const productTemplates: ProductTemplates = {
				template_ids: [...checkedList].map(t => t.toString()),
				item_id: currentActiveProduct.item_id
			};

			try {
				await updateProductTemplates(productTemplates);

				editChildProductRequest &&
					(await props.onSubmit(editChildProductRequest));

				ZMessage.success("You have successfully updated Product");
			} catch (error) {
				console.error(error);
			} finally {
				setIsModalVisible(false);
				dispatch(ProductsActionCreators.hideProductsLoader());
			}
		}
	};

	const handleCancel = () => {
		setTemplatesToUpdate([]);
		setUpdateExistingTemplateChecked(false);
		setCheckedList([]);
		setIsModalVisible(false);
	};

	const handleSelectedTemplates = (
		templatesSelected: { templateName: string; templateId: string }[]
	) => {
		setCheckedList(
			[...templatesSelected].map(template => template.templateId)
		);
	};

	const handleTaxCategoryDrawerOnSubmit = (id: string) => {
		props.form.setFieldsValue({
			tax_category_id: id
		});
	};

	const defaultData = {
		...props.currentActiveProduct?.item,
		...props.currentActiveProduct
	} as BundleProduct;

	const isReadonlyValue = !!props.isReadonly;

	return (
		<>
			<ZDrawer
				visible={props.visible}
				closable={true}
				destroyOnClose={true}
				onClose={onClose}
				className="edit-product-drawer zomentum-drawer"
				title={
					<Row>
						<Col span={24}>
							<div className="drawer-title-container">
								<ZText className="drawer-title">
									Edit Product{" "}
								</ZText>
							</div>
						</Col>
					</Row>
				}
			>
				<Skeleton
					loading={!props.currentActiveProduct?.item_id}
					active
					paragraph={{ rows: 12 }}
				>
					<AddTaxCategoryDrawer
						visible={addTaxCategoryDrawerOpen}
						onClose={closeAddTaxCategoryDrawer}
						handleTaxCategoryDrawerOnSubmit={
							handleTaxCategoryDrawerOnSubmit
						}
					/>

					{!!props.currentActiveProduct?.item_id?.length && (
						<Form
							layout="horizontal"
							labelCol={{ span: 6 }}
							wrapperCol={{ span: 18 }}
							onSubmit={onSubmit}
							className="edit-product-drawer-form"
						>
							<Row className="drawer-form-container pb-4">
								<Col span={24}>
									<Form.Item
										label="Name"
										labelAlign="left"
										hasFeedback={true}
									>
										{props.form.getFieldDecorator("name", {
											rules: [
												{
													required: true,
													message:
														"Please enter product name",
													whitespace: true
												}
											],
											initialValue:
												defaultData?.name ?? ""
										})(
											<ZInput
												disabled={isReadonlyValue}
												placeholder="Please enter product name"
											/>
										)}
									</Form.Item>
								</Col>
								{!!customFieldList?.length &&
									customFieldList.map(customField => {
										return (
											<Fragment key={customField.id}>
												{getProductFormItem(
													customField
												)}
											</Fragment>
										);
									})}
							</Row>
							<div className="ant-drawer-footer">
								<div
									className={`w-full flex justify-start items-center`}
								>
									{!props.isReadonly && (
										<ZCheckbox
											className={`pl-2`}
											onChange={e =>
												setUpdateExistingTemplateChecked(
													e.target.checked
												)
											}
											checked={
												updateExistingTemplateChecked
											}
										>
											Update Values in existing templates
										</ZCheckbox>
									)}
								</div>

								<ZButton
									type="default"
									htmlType="button"
									onClick={onClose}
									disabled={isLoading}
									className={isReadonlyValue ? "" : "mr-4"}
								>
									{isReadonlyValue ? "Close" : "Cancel"}
								</ZButton>
								{!isReadonlyValue && (
									<ZButton
										data-testid="save-button"
										type="primary"
										htmlType="submit"
										disabled={isLoading}
										loading={isLoading}
									>
										Save
									</ZButton>
								)}
							</div>
						</Form>
					)}
				</Skeleton>
			</ZDrawer>

			<ZModal
				modalBodyClassName="px-6 pt-4 pb-0"
				size={`md`}
				className={`template-confirm-modal`}
				title="Template updates"
				visible={isModalVisible}
				onOk={handleOk}
				onCancel={handleCancel}
				footer={
					<div className="flex align-center justify-between px-2 py-1.5">
						<ZText colorVariant={7} className="flex items-center">
							{checkedList.length + " Templates Selected"}
						</ZText>
						<div className="flex items-center justify-end">
							<ZButton key="back" onClick={handleCancel}>
								Cancel
							</ZButton>
							<ZButton
								key="submit"
								type="primary"
								onClick={handleOk}
							>
								Update
							</ZButton>
						</div>
					</div>
				}
			>
				<div className="mb-4">
					<ZText>The selected templates will be updated</ZText>
				</div>

				<ZEditOrCloneProductTemplateUpdateTable
					onDataSelection={handleSelectedTemplates}
					templatesToUpdate={templatesToUpdate}
				/>
			</ZModal>
		</>
	);
};

export default Form.create<Props>({ name: "edit_product_form" })(
	(props: Props) => {
		return <ConfigureBundleChildProductDrawerForm {...props} />;
	}
);
